import { useNavigate } from "react-router-dom";
import { useStoreState } from "ezStore";
import { useApolloClient, useMutation } from "@apollo/client";
import { intl } from "LocaleWrapper";

import { Card, PageHeader, Table, Button } from "@cofano/cck";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";

import { TableId, useBackendTable } from "components/table";
import { useConfirm } from "components/hooks/useConfirm";
import Helmet from "components/helmet/Helmet";
import { isReadOnly } from "components/utils/Permission";

import LocationSelect from "view/locations/components/selects/LocationSelect";

import { DataFormat } from "imports/Utils";

import LocationAddressPopover from "./components/popover/LocationAddressPopover";
import LocationGroupsPopover from "./components/popover/LocationGroupsPopover";
import LocationTypeSelect from "./components/selects/LocationTypeSelect";
import { getLocationTypeNumber, hasEditLocationPermission } from "./utils/LocationUtils";

import { ChildPropsFromPagination } from "types/commons";

import { SearchLocationsDocument, SearchLocationsQuery } from "__generated__/view/locations/graphql/location.queries.generated";
import { DeleteLocationDocument } from "__generated__/view/locations/graphql/location.mutations.generated";

export type LocationFromQuery = ChildPropsFromPagination<SearchLocationsQuery, "searchLocations">

const Locations = () => {

	const confirm = useConfirm();
	const navigate = useNavigate();
	const client = useApolloClient();
	const session = useStoreState(state => state.session);

	const canEdit = hasEditLocationPermission(session);
	const canAdd = !isReadOnly(session);

	const { tableProps, refetchAfterDelete } = useBackendTable({
		id: TableId.Locations,
		onChange: async(pagination, sort, filter, callback) => {
			try {
				const result = await client.query({
					query: SearchLocationsDocument,
					variables: {
						pagination: {
							...pagination,
							sortList: sort,
						},
						locationSearch: {
							...filter,
							types: filter.types ? [ getLocationTypeNumber(filter.types) ] : undefined,
						},
					},
				});

				callback({
					content: result.data.searchLocations.content,
					totalResults: result.data.searchLocations.totalElements,
				});
			} catch {
				callback({
					content: [],
					totalResults: 0,
				});
			}
		},
	});

	const [ deleteLocation ] = useMutation(DeleteLocationDocument, {
		onCompleted: () => refetchAfterDelete(),
	});

	return (
		<div>
			<PageHeader>
				<PageHeader.Title>
					{intl.formatMessage({ id: "LOCATIONS" })}
					<Helmet title={intl.formatMessage({ id: "LOCATIONS" })} />
				</PageHeader.Title>

				<PageHeader.Actions>
					{canAdd && (
						<Button onClick={() => navigate("/view/locations/create")}>
							{intl.formatMessage({ id: "ADD_BUTTON" })}
						</Button>
					)}
				</PageHeader.Actions>
			</PageHeader>

			<Card disablePadding>
				<Table
					{...tableProps}
					onRowDoubleClick={(row, event) => {
						const url = `/view/locations/${row.id}/details`;
						if (event.ctrlKey) {
							window.open(url);
						} else {
							navigate(url);
						}
					}}
					columns={[ {
						id: "description",
						title: intl.formatMessage({ id: "DESCRIPTION" }),
						sortable: true,
						filterComponent: "TEXT",
					}, {
						id: "code",
						title: intl.formatMessage({ id: "CODE" }),
						sortable: true,
						filterComponent: "TEXT",
					}, {
						id: "types",
						title: intl.formatMessage({ id: "TYPE" }),
						render: (row: LocationFromQuery) => intl.formatMessage({ id: row.type ?? "" }),
						filterComponent: (value, onChange) => (
							<LocationTypeSelect
								label={intl.formatMessage({ id: "LOCATION_TYPE" })}
								value={value}
								onChange={onChange} />
						),
					}, {
						id: "locationGroups",
						title: intl.formatMessage({ id: "LOCATION_GROUP" }),
						render: (row: LocationFromQuery) => <LocationGroupsPopover locationGroups={row.locationGroups} />,
						filterComponent: (value, onChange) => (
							<LocationSelect
								value={value}
								onChange={onChange}
								label={intl.formatMessage({ id: "LOCATION_GROUP" })} />
						),
					}, {
						id: "address.street",
						title: intl.formatMessage({ id: "ADDRESS" }),
						render: (row: LocationFromQuery) => <LocationAddressPopover address={row.address ?? null} />,
					}, {
						id: "externalId",
						title: intl.formatMessage({ id: "EXTERNAL_ID" }),
						sortable: true,
						filterComponent: "TEXT",
					}, {
						id: "isActive",
						title: intl.formatMessage({ id: "ACTIVE" }),
						render: (row: LocationFromQuery) => DataFormat.boolean(row.isActive),
						filterComponent: "BOOLEAN",
					} ]}
					rowActions={[ {
						icon: <EditIcon />,
						onClick: (row) => navigate(`/view/locations/${row.id}/edit`),
						hidden: !canEdit,
					}, {
						hidden: (row) => !canEdit || !!row.externalId,
						icon: <DeleteIcon />,
						onClick: (row) => {
							confirm({
								type: "DELETE",
							}).then(() => {
								deleteLocation({
									variables: {
										id: row.id,
									},
								});
							});
						},
					} ]} />
			</Card>
		</div>
	);
};

export default Locations;
